import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import * as d3 from 'd3';

export interface ChartData {
    x: number;
    yColorLine?: number;
    yGreyLine?: number;
    yBar?: number;
}

interface YAxis {
    y0: number;
    y1: number;
    label?: string;
}

interface XAxis {
    x0: number;
    x1: number;
    label?: string;
}

@Component({
    selector: 'lib-mixed-chart',
    templateUrl: './mixed-chart.component.html',
    styleUrls: ['./mixed-chart.component.scss']
})
export class MixedChartComponent implements OnInit, OnChanges {
    xAxis: XAxis = {
        x0: 0,
        x1: 1
    };
    yAxis: YAxis = {
        y0: 0,
        y1: 1
    };
    @Input() chartData: { xAxis: XAxis, yAxis: YAxis, data: ChartData[] } = {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        data: []
    };

    @Input() companyName: any = '';

    constructor() {
    }

    ngOnInit(): void {
        this.draw(this.chartData.data);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.chartData && !changes.chartData.firstChange && changes.chartData?.currentValue?.data) {
            this.draw(changes.chartData.currentValue?.data);
        }
    }

    draw(data: any) {
        d3.select('svg#mixed').remove();
        const w = d3.select('div#mixedWrapper');
        w.append('svg')
            .attr('id', 'mixed');
        const margin = {top: 30, right: 20, bottom: 40, left: 50};
        const width = 900 - margin.left - margin.right;
        const height = 523 - margin.top - margin.bottom;

        // Create the SVG container.
        const svg = d3.select('svg#mixed')
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', [0, 0, width, height])
            .attr('style', 'max-width: 100%; height: auto;');
        svg.enter().remove();

        const x: any = d3.scaleBand()
            .domain(data.map((d: any) => d.x))
            .rangeRound([margin.left, width - margin.right])
            .padding(0);

        const yColorLine: any = d3.scaleLinear()
            .domain([d3.min(data, (el: any) => el.yColorLine as number) as number, d3.max(data, (el: any) => el.yColorLine as number) as number])
            .rangeRound([height - margin.bottom, margin.top]);

        const yGreyLine: any = d3.scaleLinear()
            .domain([d3.min(data, (el: any) => el.yGreyLine as number) as number, d3.max(data, (el: any) => el.yGreyLine as number) as number])
            .rangeRound([height - margin.bottom, margin.top]);

        const yBar: any = d3.scaleLinear()
            .domain([this.yAxis.y0, this.yAxis.y1])
            .domain([d3.min(data, (el: any) => el.yBar as number) as number, d3.max(data, (el: any) => el.yBar as number) as number])
            .rangeRound([height - margin.bottom, margin.top]);

        const xAxis = (g: any) => g
            .attr('transform', `translate(0,${height - margin.bottom})`)
            .style('color', '#000')
            .call(d3.axisBottom(x)
                .tickSize(0)
                .ticks(0)
                .tickSizeOuter(0)
                .tickFormat(() => '')
            );

        const yAxis = (g: any) => g
            .attr('transform', `translate(${margin.left},0)`)

            .call(d3.axisLeft(yBar)
                .tickSizeOuter(0)
                .ticks(0)
            );


        // const yGreyLineAxis = (g: any) => g
        //     .call(d3.axisRight(yGreyLine))
        //     .call((gd: any) => gd.select('.domain').remove())
        //     .call((gt: any) => gt.append('text')
        //         .attr('x', margin.right)
        //         .attr('y', 10)
        //         .attr('fill', 'currentColor')
        //         .attr('text-anchor', 'end')
        //         .text(data.yGreyLine));

        const colorLine = d3.line()
            .x((d: any) => x(d.x) + x.bandwidth() / 2)
            .y((d: any) => {
                return yColorLine(d.yColorLine);
            });

        const greyLine = d3.line()
            .x((d: any) => x(d.x) + x.bandwidth() / 2)
            .y((d: any) => yGreyLine(d.yGreyLine));


        svg.append('g')
            .attr('fill', '#E3E3E3')
            .attr('fill-opacity', 0.8)
            .selectAll('rect')
            .data(data)
            .join('rect')
            .attr('x', (d: any) => x(d.x))
            .attr('y', (d: any) => yBar(d.yBar))
            .attr('width', x.bandwidth())
            .attr('height', (d: any) => {
                return height - yBar(d.yBar) - margin.bottom;
            });

        svg.append('path')
            .attr('fill', 'none')
            .attr('stroke-miterlimit', 1)
            .attr('stroke-width', 5)
            .style('stroke', `#9F9CAC`)
            .attr('d', greyLine(data));

        svg.append('path')
            .attr('fill', 'none')
            .attr('id', 'colorLine')
            .attr('stroke-miterlimit', 1)
            .attr('stroke-width', 5)
            .style('stroke', `url(#sun-gradient)`)
            .attr('d', colorLine(data));

        svg.append('g')
            .attr('fill', 'none')
            .attr('pointer-events', 'all')
            .selectAll('rect')
            .data(data)
            .join('rect')
            .attr('x', (d: any) => x(d.x))
            .attr('width', x.bandwidth())
            .attr('y', 0)
            .attr('height', height)
            .append('title');
        // .text((d: any) => d.x);

        svg.append('g')
            .attr('id', 'axisX')
            .call(xAxis);

        svg.append('g')
            .call(yAxis)
            .attr('id', 'axisY');


        svg.append('text')
            .attr('class', 'x label')
            .attr('text-anchor', 'start')
            .attr('x', margin.left)
            .attr('y', height - 10)
            .attr('font-family', 'Raleway')
            .attr('font-size', '24px')
            .attr('font-weight', '400')
            .style('fill', '#10082F')
            .text(this.chartData.xAxis?.label || '');

        svg.append('text')
            .attr('class', 'y label')
            .attr('text-anchor', 'start')
            .attr('x', -(height - margin.bottom))
            .attr('y', 10)
            .attr('dy', '.75em')
            .attr('transform', 'rotate(-90)')
            .attr('font-family', 'Raleway')
            .attr('font-size', '24px')
            .attr('font-weight', '400')
            .style('fill', '#10082F')
            .text(this.chartData.yAxis?.label || '');
        //
        // svg.append('g')
        //     .call(yGreyLineAxis);


        svg.append('defs');
        svg.call(this.createRadialGradient);
        svg.call(this.createArrows);
        svg.select('#axisX path.domain')
            .attr('marker-end', 'url(#arrowhead-right)');

        svg.select('#axisY path.domain')
            .attr('marker-end', 'url(#arrowhead-top)');
        this.appendLegend(svg, margin);

    }

    createArrows(select: any) {
        const arrowX = select
            .select('defs');
        arrowX
            .append('marker')
            .attr('id', 'arrowhead-right')
            .attr('refX', 5)
            .attr('refY', 5)
            .attr('markerWidth', 16)
            .attr('markerHeight', 16)
            .append('path')
            .attr('d', 'M 0 0 L 5 5 L 0 10')
            .attr('stroke', 'black')
            .attr('orient', 'auto-star')
            .attr('stroke-width', 1)
            .attr('fill', 'none');

        const arrowY = select
            .select('defs');
        arrowY
            .append('marker')
            .attr('id', 'arrowhead-top')
            .attr('refX', 3)
            .attr('refY', 5)
            .attr('orient', '-90deg')
            .attr('markerWidth', 16)
            .attr('markerHeight', 13)
            .append('path')
            .attr('d', 'M 0 0 L 5 5 L 0 10')
            .attr('stroke', 'black')
            .attr('stroke-width', 1)
            .attr('fill', 'none');

    }

    appendLegend(svg: any, margin: any) {

        svg.append('g')
            .append('rect')
            .attr('width', 80)
            .attr('height', 5)
            .attr('x', margin.left + 40)
            .attr('y', 50)
            .attr('fill', `url(#sun-gradient)`);
        svg
            .append('text')
            .attr('x', margin.left + 40)
            .attr('y', 70)
            .attr('font-family', 'Raleway')
            .attr('font-size', '12px')
            .text(this.companyName.toUpperCase());


        svg.append('g')
            .append('rect')
            .attr('width', 80)
            .attr('height', 5)
            .attr('x', margin.left + 40)
            .attr('y', 100)
            .attr('fill', `#9F9CAC`);
        svg
            .append('text')
            .attr('x', margin.left + 40)
            .attr('y', 120)
            .attr('font-family', 'Raleway')
            .attr('font-size', '12px').text('Ukraine');
    }

    createRadialGradient(select: any) {
        const gradient = select
            .select('defs');
        gradient
            .append('linearGradient')
            .attr('id', 'sun-gradient')
            .attr('cx', '100%')    // not really needed, since 50% is the default
            .attr('cy', '100%')    // not really needed, since 50% is the default
            .selectAll('stop')
            .data([
                {offset: '0%', color: '#FFF847'},
                {offset: '50%', color: '#14FF80'},
                {offset: '100%', color: '#FF5757'},
            ])
            .enter().append('stop')
            .attr('offset', (d: any) => d.offset)
            .attr('stop-color', (d: any) => d.color);
    }
}
